// src/pages/PagesUtilisateurs/DashboardUsers/modules/MessagesDisplay.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCheck,
  FaCheckDouble,
  FaEnvelope,
  FaEyeSlash,
  FaTrash,
} from "react-icons/fa";

import { LuMessageSquarePlus } from "react-icons/lu";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  deleteMessageById,
  getMessagesByUser,
  markAllMessagesAsRead,
  markMessageAsRead,
} from "../../../../services/messageServices";

import { useMessage } from "../../../../hooks/MessageContext";

import DashboardActionButton from "../ui/DashboardActionButton";
import DashboardEmptyState from "../ui/DashboardEmptyState";

import {
  formatDateTime,
  isReadFlag,
} from "../utils/dashboardUser.utils";

/* -------------------------------------------------------------------------- */
/*                              MessageContent                                */
/* -------------------------------------------------------------------------- */

function MessageContent({ content }) {
  if (!content) {
    return (
      <p className="text-sm italic text-slate-400">
        Aucun contenu.
      </p>
    );
  }

  return (
    <div
      className="
        max-w-full
        break-words
        [overflow-wrap:anywhere]

        text-sm
        leading-6
        text-slate-600

        dark:text-slate-300

        [&_a]:font-semibold
        [&_a]:text-[#013E8E]
        [&_a]:underline
        [&_a]:underline-offset-2

        dark:[&_a]:text-blue-300

        [&_blockquote]:my-3
        [&_blockquote]:border-l-4
        [&_blockquote]:border-slate-200
        [&_blockquote]:pl-4
        [&_blockquote]:italic

        dark:[&_blockquote]:border-slate-700

        [&_br]:leading-7

        [&_ol]:my-3
        [&_ol]:list-decimal
        [&_ol]:pl-5

        [&_p]:my-2
        [&_p:first-child]:mt-0
        [&_p:last-child]:mb-0

        [&_strong]:font-bold
        [&_strong]:text-slate-800

        dark:[&_strong]:text-slate-100

        [&_ul]:my-3
        [&_ul]:list-disc
        [&_ul]:pl-5
      "
      dangerouslySetInnerHTML={{
        __html: content,
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/*                              MessagesDisplay                               */
/* -------------------------------------------------------------------------- */

export default function MessagesDisplay({
  userId,
  onCounts,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const { setUnreadMessageCount } = useMessage();

  const navigate = useNavigate();

  /* ---------------------------------------------------------------------- */
  /* Chargement                                                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        setLoading(true);

        const data = await getMessagesByUser(userId);

        if (!mounted) return;

        /*
         * On ne conserve que les messages reçus.
         * Les copies correspondent aux messages envoyés.
         */
        const filtered = (
          Array.isArray(data) ? data : []
        ).filter((message) => !message.is_copy);

        setMessages(filtered);
      } catch (error) {
        console.error(
          "Erreur chargement messages :",
          error,
        );

        toast.error(
          "Erreur lors du chargement des messages.",
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userId]);

  /* ---------------------------------------------------------------------- */
  /* Compteurs                                                              */
  /* ---------------------------------------------------------------------- */

  const { total, unread } = useMemo(() => {
    const totalValue = messages.length;

    const unreadValue = messages.filter(
      (message) =>
        !isReadFlag(message.read_message),
    ).length;

    return {
      total: totalValue,
      unread: unreadValue,
    };
  }, [messages]);

  useEffect(() => {
    setUnreadMessageCount(unread);

    onCounts?.({
      total,
      unread,
    });
  }, [
    total,
    unread,
    setUnreadMessageCount,
    onCounts,
  ]);

  /* ---------------------------------------------------------------------- */
  /* Changer état lu / non lu                                               */
  /* ---------------------------------------------------------------------- */

  const toggleReadStatus = async (id) => {
    try {
      await markMessageAsRead(id);

      setMessages((previous) =>
        previous.map((message) =>
          message.id === id
            ? {
                ...message,

                read_message: !isReadFlag(
                  message.read_message,
                ),
              }
            : message,
        ),
      );
    } catch (error) {
      console.error(
        "Erreur changement état message :",
        error,
      );

      toast.error(
        "Impossible de modifier l'état du message.",
      );
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Suppression                                                            */
  /* ---------------------------------------------------------------------- */

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Supprimer ce message ?",
    );

    if (!confirmed) return;

    try {
      await deleteMessageById(id);

      setMessages((previous) =>
        previous.filter(
          (message) => message.id !== id,
        ),
      );

      toast.success("Message supprimé.");
    } catch (error) {
      console.error(
        "Erreur suppression message :",
        error,
      );

      toast.error(
        "Erreur lors de la suppression.",
      );
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Tout marquer comme lu                                                  */
  /* ---------------------------------------------------------------------- */

  const handleMarkAllAsRead = async () => {
    if (!unread) return;

    try {
      await markAllMessagesAsRead(userId);

      setMessages((previous) =>
        previous.map((message) => ({
          ...message,
          read_message: true,
        })),
      );

      toast.success(
        "Tous les messages sont marqués comme lus.",
      );
    } catch (error) {
      console.error(
        "Erreur lecture de tous les messages :",
        error,
      );

      toast.error(
        "Impossible de tout marquer comme lu.",
      );
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Chargement                                                             */
  /* ---------------------------------------------------------------------- */

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({
          length: 4,
        }).map((_, index) => (
          <div
            key={index}
            className="
              h-28
              animate-pulse
              rounded-2xl
              bg-slate-100
              dark:bg-slate-800
            "
          />
        ))}
      </div>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <div className="w-full min-w-0 max-w-full space-y-4">
      {/* ------------------------------------------------------------------ */}
      {/* Toolbar                                                            */}
      {/* ------------------------------------------------------------------ */}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            {total} message
            {total > 1 ? "s" : ""} reçu
            {total > 1 ? "s" : ""}
          </p>

          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {unread > 0
              ? `${unread} non lu${
                  unread > 1 ? "s" : ""
                }`
              : "Aucun nouveau message"}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <DashboardActionButton
            icon={FaCheckDouble}
            onClick={handleMarkAllAsRead}
            disabled={!unread}
          >
            Tout marquer lu
          </DashboardActionButton>

          <DashboardActionButton
            icon={LuMessageSquarePlus}
            variant="primary"
            onClick={() =>
              navigate("/messages")
            }
          >
            Messagerie
          </DashboardActionButton>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Aucun message                                                      */}
      {/* ------------------------------------------------------------------ */}

      {!messages.length ? (
        <DashboardEmptyState
          icon={FaEnvelope}
          title="Aucun message"
          description="Vos conversations avec les enseignants et les responsables du club apparaîtront ici."
        />
      ) : (
        <>
          {/* ---------------------------------------------------------------- */}
          {/* Desktop                                                          */}
          {/* ---------------------------------------------------------------- */}

          <div
            className="
              hidden
              w-full
              min-w-0
              max-w-full
              overflow-hidden
              rounded-2xl
              border border-slate-200

              md:block

              dark:border-slate-800
            "
          >
            <table className="w-full table-fixed">
              <thead className="bg-slate-50 dark:bg-slate-950/50">
                <tr
                  className="
                    text-left
                    text-[10px]
                    font-black
                    uppercase
                    tracking-[0.14em]
                    text-slate-400
                  "
                >
                  <th className="w-24 px-4 py-3">
                    Date
                  </th>

                  <th className="px-4 py-3">
                    Message
                  </th>

                  <th className="w-28 px-4 py-3 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {messages.map((message) => {
                  const read = isReadFlag(
                    message.read_message,
                  );

                  const { date, time } =
                    formatDateTime(
                      message.created_at,
                    );

                  const sender = [
                    message?.Sender?.prenom,
                    message?.Sender?.nom,
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <tr
                      key={message.id}
                      className={`
                        transition

                        hover:bg-slate-50

                        dark:hover:bg-slate-800/50

                        ${
                          read
                            ? ""
                            : `
                              bg-blue-50/40
                              dark:bg-blue-500/5
                            `
                        }
                      `}
                    >
                      {/* Date */}

                      <td className="px-4 py-4 align-top">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">
                          {date}
                        </p>

                        <p className="mt-0.5 text-[10px] text-slate-400">
                          {time}
                        </p>
                      </td>

                      {/* Message */}

                      <td className="min-w-0 px-4 py-4 align-top">
                        <div className="flex min-w-0 gap-3">
                          {!read ? (
                            <span
                              className="
                                mt-2
                                h-2 w-2
                                shrink-0
                                rounded-full
                                bg-blue-500
                              "
                            />
                          ) : null}

                          <div className="min-w-0 flex-1">
                            {/* Expéditeur */}

                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-xs font-bold text-red-500 dark:text-red-400">
                                {sender || "Club"}
                              </p>

                              {!read ? (
                                <span
                                  className="
                                    rounded-full
                                    bg-blue-100
                                    px-2 py-0.5
                                    text-[9px]
                                    font-semibold
                                    uppercase
                                    tracking-wide
                                    text-[#013E8E]

                                    dark:bg-blue-500/10
                                    dark:text-blue-300
                                  "
                                >
                                  Nouveau
                                </span>
                              ) : null}
                            </div>

                            {/* Sujet */}

                            <h4
                              className="
                                mt-1
                                break-words
                                [overflow-wrap:anywhere]

                                text-sm
                                font-black
                                text-slate-950

                                dark:text-white
                              "
                            >
                              {message.subject ||
                                "Sans objet"}
                            </h4>

                            {/* Contenu complet */}

                            <div
                              className="
                                mt-3
                                rounded-xl
                                border border-slate-100
                                bg-slate-50/60
                                p-3

                                dark:border-slate-800
                                dark:bg-slate-950/40
                              "
                            >
                              <MessageContent
                                content={
                                  message.content
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}

                      <td className="px-4 py-4 align-top">
                        <div className="flex justify-end gap-2">
                          <DashboardActionButton
                            compact
                            icon={
                              read
                                ? FaEyeSlash
                                : FaCheck
                            }
                            onClick={() =>
                              toggleReadStatus(
                                message.id,
                              )
                            }
                            title={
                              read
                                ? "Marquer comme non lu"
                                : "Marquer comme lu"
                            }
                          />

                          <DashboardActionButton
                            compact
                            icon={FaTrash}
                            variant="danger"
                            onClick={() =>
                              handleDelete(
                                message.id,
                              )
                            }
                            title="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Mobile                                                           */}
          {/* ---------------------------------------------------------------- */}

          <div className="space-y-3 md:hidden">
            {messages.map((message) => {
              const read = isReadFlag(
                message.read_message,
              );

              const { date, time } =
                formatDateTime(
                  message.created_at,
                );

              const sender = [
                message?.Sender?.prenom,
                message?.Sender?.nom,
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <article
                  key={message.id}
                  className={`
                    w-full
                    min-w-0
                    max-w-full
                    overflow-hidden
                    rounded-2xl
                    border
                    p-4

                    ${
                      read
                        ? `
                          border-slate-200
                          bg-white

                          dark:border-slate-800
                          dark:bg-slate-900
                        `
                        : `
                          border-blue-200
                          bg-blue-50/50

                          dark:border-blue-500/20
                          dark:bg-blue-500/5
                        `
                    }
                  `}
                >
                  {/* Header */}

                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-slate-600 dark:text-slate-300">
                        {date}
                      </p>

                      <p className="text-[10px] text-slate-400">
                        {time}
                      </p>
                    </div>

                    {!read ? (
                      <span
                        className="
                          rounded-full
                          bg-blue-100
                          px-2.5 py-1
                          text-[9px]
                          font-black
                          uppercase
                          tracking-wide
                          text-[#013E8E]

                          dark:bg-blue-500/10
                          dark:text-blue-300
                        "
                      >
                        Nouveau
                      </span>
                    ) : null}
                  </div>

                  {/* Expéditeur */}

                  <div className="mt-3">
                    <p
                      className="
                        text-[10px]
                        font-black
                        uppercase
                        tracking-[0.12em]
                        text-slate-400
                      "
                    >
                      De
                    </p>

                    <p
                      className="
                        mt-1
                        text-sm
                        font-bold
                        text-red-700

                        dark:text-red-400
                      "
                    >
                      {sender || "Club"}
                    </p>
                  </div>

                  {/* Sujet */}

                  <div className="mt-3">
                    <p
                      className="
                        text-[10px]
                        font-black
                        uppercase
                        tracking-[0.12em]
                        text-slate-400
                      "
                    >
                      Objet
                    </p>

                    <h4
                      className="
                        mt-1
                        break-words
                        [overflow-wrap:anywhere]

                        font-black
                        leading-5
                        text-slate-950

                        dark:text-white
                      "
                    >
                      {message.subject ||
                        "Sans objet"}
                    </h4>
                  </div>

                  {/* Message complet */}

                  <div
                    className="
                      mt-4
                      min-w-0
                      max-w-full
                      rounded-2xl
                      border border-slate-100
                      bg-white
                      p-3.5

                      dark:border-slate-800
                      dark:bg-slate-950/50
                    "
                  >
                    <MessageContent
                      content={
                        message.content
                      }
                    />
                  </div>

                  {/* Actions */}

                  <div
                    className="
                      mt-4
                      flex
                      flex-wrap
                      justify-end
                      gap-2

                      border-t
                      border-slate-100
                      pt-3

                      dark:border-slate-800
                    "
                  >
                    <DashboardActionButton
                      icon={
                        read
                          ? FaEyeSlash
                          : FaCheck
                      }
                      onClick={() =>
                        toggleReadStatus(
                          message.id,
                        )
                      }
                    >
                      {read
                        ? "Marquer non lu"
                        : "Marquer lu"}
                    </DashboardActionButton>

                    <DashboardActionButton
                      compact
                      icon={FaTrash}
                      variant="danger"
                      onClick={() =>
                        handleDelete(
                          message.id,
                        )
                      }
                      title="Supprimer"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}